/**
 * Painel de Ads do ML — tipos (espelho das rotas /api/ads/*) e regras puras da tela.
 * Spec: entrega_dev_ads_ml/specs/2026-10-09-ads-ml-design.md (§7 e §8). Conferência: ./logic.check.mjs
 *
 * O farol e as somas são calculados no serviço (core/ads.py); aqui só há janela do período,
 * formatos, ordenação e filtros — nada que recalcule KPI (regra de UX dela: o painel é o mapa, a lista é a lista).
 */
import type { PeriodQuery } from '../../api/bridge'

export type Farol = 'verde' | 'amarelo' | 'vermelho' | 'cinza'

export interface Metricas {
  prints: number
  clicks: number
  cost: number
  direct_amount: number
  indirect_amount: number
  total_amount: number
  direct_items_quantity: number
  indirect_items_quantity: number
  direct_units_quantity: number
  indirect_units_quantity: number
  acos: number | null
  roas: number | null
  cpc: number | null
  ctr: number | null
  itens: number
  /** unidades vendidas (direta + indireta) — pedido dela, 20/10/2026 */
  unidades: number
  /** gasto ÷ unidades vendidas; null sem venda (não é zero) */
  custo_unidade: number | null
  nulos: number
}

export interface Kpis extends Metricas {
  conversao: number | null
}

export interface BlocoConta extends Kpis {
  conta?: string
  /** dias com linha na base no período; 0 = sem dado (não é zero) */
  dias_com_dado?: number
  anterior: Kpis
  delta: Partial<Record<keyof Kpis, number | null>>
}

export interface ResumoAds {
  periodo: { ini: string; fim: string; parcial: boolean }
  periodo_anterior?: { ini: string; fim: string }
  vazio: boolean
  motivo?: string
  contas: Record<string, BlocoConta>
  total: BlocoConta | null
  tacos: Record<string, number | null> & { faturamento?: Record<string, number | string> }
}

export interface CampanhaLinha extends Kpis {
  user_id: string
  conta: string
  campaign_id: number
  nome: string | null
  status: string | null
  strategy: string | null
  roas_target: number | null
  acos_target: number | null
  acos_alvo: number | null
  budget: number | null
  automatic_budget: boolean | null
  farol: Farol
  hold_count: number
  impression_share: number | null
  top_impression_share: number | null
  lost_impression_share_by_budget: number | null
  lost_impression_share_by_ad_rank: number | null
  dias_com_dado: number
  /** conversão do anúncio inteiro dos itens da campanha: pedidos (margens) ÷ visitas (API) — comparar com a do Ads */
  anuncios_na_campanha: number
  anuncios_com_visita: number
  visitas: number | null
  pedidos: number
  conversao_anuncio: number | null
}

export interface AnuncioLinha extends Metricas {
  user_id: string
  conta: string
  item_id: string
  titulo: string
  campaign_id: number | null
  campanha_nome: string | null
  status_ads: string | null
  price: number | null
  logistic_type: string | null
  conversao_ads: number | null
  visitas: number | null
  pedidos: number
  faturamento: number
  conversao_anuncio: number | null
  motivo?: string
}

export interface CurvaALinha {
  user_id: string
  conta: string
  item_id: string
  titulo: string
  faturamento_30d: number
  pedidos_30d: number
  visitas_30d: number | null
  conversao_anuncio: number | null
  motivo: string
  status_ads: string | null
}

export interface PontoGasto {
  data: string
  total: Kpis
  [uid: string]: Kpis | string
}

export interface MesContraMes {
  disponivel: boolean
  serie_desde: string | null
  periodos?: { atual: [string, string]; anterior: [string, string] }
  [uid: string]: unknown
}

export interface GastoDiario {
  serie: PontoGasto[]
  mes_contra_mes: MesContraMes
}

export interface ColetaStatusJson {
  ok: boolean | null
  por?: string
  iniciado_em?: string
  terminado_em?: string | null
  segundos?: number
  nulos?: number
  hoje?: string
  contas?: Record<string, Record<string, unknown>>
  curva_a_visitas?: number | string
}

export interface ColetaStatus {
  rodando: boolean
  iniciado_em?: string | null
  por?: string | null
  terminado_em?: string | null
  rc?: number | null
  erro?: string | null
  status: ColetaStatusJson | null
  minutos_desde_ultima_ok?: number | null
}

export const CONTAS: Record<string, string> = { '777058595': 'ML Matriz', '3131364517': 'ML Extrema' }
export const UIDS = Object.keys(CONTAS)

export function nomeConta(uid: string): string {
  return CONTAS[uid] || uid
}

/* ------------------------------------------------------------------ período do cabeçalho */

function iso(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

function menosDias(d: Date, n: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  x.setDate(x.getDate() - n)
  return x
}

/**
 * Converte o período global do BI num intervalo de dias para as rotas de Ads.
 * 7d/30d = dias FECHADOS até D-1 (o Meli consolida às 10h). hoje/semana/mes incluem o dia corrente → parcial.
 */
export function intervaloDoPeriodo(q: PeriodQuery, hoje: Date): { ini: string; fim: string; parcial: boolean } {
  const hojeIso = iso(hoje)
  if (q.dataInicio && q.dataFim) {
    return { ini: q.dataInicio, fim: q.dataFim, parcial: q.dataFim >= hojeIso }
  }
  const p = q.periodo || '7d'
  if (p === 'hoje') return { ini: hojeIso, fim: hojeIso, parcial: true }
  if (p === 'semana') {
    const dow = hoje.getDay() // 0 = domingo
    const desdeSegunda = (dow + 6) % 7
    return { ini: iso(menosDias(hoje, desdeSegunda)), fim: hojeIso, parcial: true }
  }
  if (p === 'mes') return { ini: iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), fim: hojeIso, parcial: true }
  const n = p === '30d' ? 30 : 7
  return { ini: iso(menosDias(hoje, n)), fim: iso(menosDias(hoje, 1)), parcial: false }
}

export function contaDoStoreKey(storeKey: string): 'ml' | 'outro' | 'todas' {
  const sk = (storeKey || '').trim()
  if (!sk) return 'todas'
  return sk.startsWith('ml_') ? 'ml' : 'outro'
}

/* ------------------------------------------------------------------ formatos */

const fmtNum = (casas: number) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })

export function pct(v: number | null | undefined, casas = 1): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `${fmtNum(casas).format(v)}%`
}

export function brl(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `R$ ${fmtNum(2).format(v)}`
}

export function inteiro(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—'
  return fmtNum(0).format(v)
}

export function deltaTexto(d: number | null | undefined): string {
  if (d == null || Number.isNaN(d)) return '—'
  const s = fmtNum(1).format(Math.abs(d))
  return d > 0 ? `+${s}%` : d < 0 ? `−${s}%` : `${s}%`
}

export function acosDe(cost: number, total: number): number | null {
  if (!total || total <= 0) return null
  return Math.round((cost / total) * 10000) / 100
}

export function conversaoDe(vendas: number, cliques: number): number | null {
  if (!cliques || cliques <= 0) return null
  return Math.round((vendas / cliques) * 10000) / 100
}

export const FAROL_LABEL: Record<Farol, string> = {
  verde: 'Na meta',
  amarelo: 'Até 25% acima',
  vermelho: 'Fora da meta',
  cinza: 'Sem meta',
}

export function farolLabel(f: Farol): string {
  return FAROL_LABEL[f] || f
}

export function farolCor(f: Farol, palette: { success: { main: string }; warning: { main: string }; error: { main: string }; text: { disabled: string } }): string {
  if (f === 'verde') return palette.success.main
  if (f === 'amarelo') return palette.warning.main
  if (f === 'vermelho') return palette.error.main
  return palette.text.disabled
}

export const STATUS_ADS_LABEL: Record<string, string> = {
  active: 'ativo',
  paused: 'pausado',
  hold: 'travado (estoque/anúncio)',
  idle: 'elegível, fora de campanha',
  delegated: 'delegado',
  revoked: 'revogado',
}

/* ------------------------------------------------------------------ ordenar e filtrar */

export type Dir = 'asc' | 'desc'

/** Ordena uma cópia; null/undefined vão sempre para o fim, em qualquer direção. */
export function ordenar<T>(linhas: T[], chave: keyof T, dir: Dir): T[] {
  const sinal = dir === 'asc' ? 1 : -1
  return [...linhas].sort((a, b) => {
    const va = a[chave] as unknown
    const vb = b[chave] as unknown
    const na = va == null
    const nb = vb == null
    if (na && nb) return 0
    if (na) return 1
    if (nb) return -1
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sinal
    return String(va).localeCompare(String(vb), 'pt-BR') * sinal
  })
}

export interface FiltroCampanhas {
  farol: Farol[]
  status: string[]
  conta: string
}

/**
 * Filtro inicial da aba Campanhas: só as ATIVAS (pedido dela, 10/10/2026: "campanha inativa não precisa subir
 * no painel, ou tem que ter um filtro de somente ativas"). Pausada/apagada continua na base e aparece ao trocar o
 * filtro para "todos" — inclusive para ler o histórico de gasto.
 */
export const FILTRO_CAMPANHAS_PADRAO: FiltroCampanhas = { farol: [], status: ['active'], conta: '' }

export function filtrarCampanhas<T extends { farol: Farol; status: string | null; user_id: string }>(linhas: T[], f: FiltroCampanhas): T[] {
  return linhas.filter(
    (l) =>
      (!f.farol.length || f.farol.includes(l.farol)) &&
      (!f.status.length || f.status.includes(l.status || '')) &&
      (!f.conta || l.user_id === f.conta),
  )
}

export function contarFarol(linhas: Array<{ farol: Farol }>): Record<Farol, number> {
  const out: Record<Farol, number> = { verde: 0, amarelo: 0, vermelho: 0, cinza: 0 }
  for (const l of linhas) out[l.farol] = (out[l.farol] || 0) + 1
  return out
}

/* ------------------------------------------------------------------ metas da conta (editáveis na tela) */

/** Metas que ela digita na tela (pedido de 09/10/2026: "meta da conta em geral"). Guardadas no navegador. */
export interface MetasConta {
  /** ACOS alvo (%) por conta; vale para os KPIs e para as campanhas SEM meta própria no Meli. */
  acos_alvo: Record<string, number | null>
  /** TACOS alvo (%) das duas contas juntas. */
  tacos_alvo: number | null
}

export const METAS_STORAGE = 'fiesta_ads_metas'

/**
 * Meta padrão da conta — regra dela, 10/10/2026: "o que é saudável para mim: ROAS acima de 20 e ACOS abaixo
 * de 5%". ROAS 20 ≡ ACOS 5% (100 ÷ 20). Vale para as duas contas enquanto ela não digitar outra na tela.
 * TACOS continua sem padrão (ela não definiu).
 */
export const META_ACOS_PADRAO = 5
export const META_ROAS_PADRAO = 20

export function metasVazias(): MetasConta {
  return { acos_alvo: { '777058595': META_ACOS_PADRAO, '3131364517': META_ACOS_PADRAO }, tacos_alvo: null }
}

export function lerMetas(raw: string | null): MetasConta {
  const base = metasVazias()
  if (!raw) return base
  try {
    const j = JSON.parse(raw) as Partial<MetasConta>
    for (const u of UIDS) {
      const v = j.acos_alvo?.[u]
      // Valor fora da faixa, vazio ou ausente volta ao padrão (5 %); nunca fica "sem meta".
      base.acos_alvo[u] = typeof v === 'number' && v > 0 && v < 100 ? v : META_ACOS_PADRAO
    }
    base.tacos_alvo = typeof j.tacos_alvo === 'number' && j.tacos_alvo > 0 && j.tacos_alvo < 100 ? j.tacos_alvo : null
  } catch {
    /* json inválido = metas padrão */
  }
  return base
}

/** Mesma régua do serviço (core/ads.farol): só distância da meta; tolerância 25 %. */
export function farolCalc(acos: number | null, alvo: number | null, cost: number, total: number, tol = 0.25): Farol {
  if (!cost || cost <= 0) return 'cinza'
  // Gasto sem venda atribuída = cinza (decisão dela, 10/10/2026): campanha muito nova ainda não dá para medir contra a meta.
  if (!total || total <= 0) return 'cinza'
  if (alvo == null || acos == null) return 'cinza'
  if (acos <= alvo) return 'verde'
  if (acos <= alvo * (1 + tol) + 1e-9) return 'amarelo'
  return 'vermelho'
}

/**
 * Campanha sem meta própria no Meli herda a meta da conta digitada na tela (marcada `meta_origem: 'conta'`).
 * Campanha com meta própria não muda: cada campanha se julga pela meta dela.
 */
export function aplicarMetaDaConta(linhas: CampanhaLinha[], metas: MetasConta): Array<CampanhaLinha & { meta_origem: 'campanha' | 'conta' | 'nenhuma' }> {
  return linhas.map((l) => {
    if (l.acos_alvo != null) return { ...l, meta_origem: 'campanha' }
    const alvo = metas.acos_alvo[l.user_id]
    if (alvo == null) return { ...l, meta_origem: 'nenhuma' }
    return { ...l, acos_alvo: alvo, farol: farolCalc(l.acos, alvo, l.cost, l.total_amount), meta_origem: 'conta' }
  })
}

/** Meta de ACOS para o KPI do topo: as duas contas → média ponderada pelo gasto; uma conta → a dela. */
export function metaAcosDoResumo(resumo: ResumoAds, metas: MetasConta): number | null {
  const uids = Object.keys(resumo.contas)
  const pares = uids.map((u) => [metas.acos_alvo[u], resumo.contas[u].cost] as const).filter(([m]) => m != null) as Array<readonly [number, number]>
  if (!pares.length) return null
  const peso = pares.reduce((x, [, c]) => x + c, 0)
  if (peso <= 0) return pares[0][0]
  return Math.round((pares.reduce((x, [m, c]) => x + m * c, 0) / peso) * 100) / 100
}

/* ------------------------------------------------------------------ grupos de campanha pela sigla do nome */

/**
 * Sigla = letras iniciais do nome da campanha, antes do primeiro dígito ou espaço
 * ("ADESC06 Kit Desc Kids" → ADESC · "MAR05 Rolo" → MAR · "N01 Arvores" → N · "H…" → H). Pedido dela, 09/10/2026.
 * Nome sem sigla (começa com dígito/espaço) → "outros".
 */
export function grupoDaCampanha(nome: string | null | undefined): string {
  const m = /^\s*([A-Za-zÀ-ÿ]+)/.exec(nome || '')
  return m ? m[1].toUpperCase() : 'outros'
}

/** Soma de várias linhas (campanhas ou anúncios): o subtotal da lista filtrada e o total de cada grupo. */
export interface Somatorio {
  linhas: number
  cost: number
  total_amount: number
  clicks: number
  itens: number
  unidades: number
  visitas: number | null
  pedidos: number
  acos: number | null
  roas: number | null
  conversao: number | null
  conversao_anuncio: number | null
  cpc: number | null
  custo_unidade: number | null
}

type LinhaSomavel = {
  cost: number; total_amount: number; clicks: number; itens: number; unidades?: number; visitas?: number | null; pedidos?: number
}

/**
 * Soma dos dias/linhas, nunca média das médias: ACOS, ROAS, conversões, CPC e custo por unidade saem das somas.
 * Visitas: só as linhas com visita coletada entram (null = nenhuma linha tem) — "dado em branco não é zero".
 */
export function somar(linhas: LinhaSomavel[]): Somatorio {
  const r: Somatorio = { linhas: 0, cost: 0, total_amount: 0, clicks: 0, itens: 0, unidades: 0, visitas: null, pedidos: 0, acos: null, roas: null, conversao: null, conversao_anuncio: null, cpc: null, custo_unidade: null }
  for (const l of linhas) {
    r.linhas += 1
    r.cost += l.cost || 0
    r.total_amount += l.total_amount || 0
    r.clicks += l.clicks || 0
    r.itens += l.itens || 0
    r.unidades += l.unidades || 0
    r.pedidos += l.pedidos || 0
    if (l.visitas != null) r.visitas = (r.visitas || 0) + l.visitas
  }
  r.cost = Math.round(r.cost * 100) / 100
  r.total_amount = Math.round(r.total_amount * 100) / 100
  r.acos = acosDe(r.cost, r.total_amount)
  r.roas = r.cost > 0 ? Math.round((r.total_amount / r.cost) * 100) / 100 : null
  r.conversao = conversaoDe(r.itens, r.clicks)
  r.conversao_anuncio = r.visitas ? conversaoDe(r.pedidos, r.visitas) : null
  r.cpc = r.clicks > 0 ? Math.round((r.cost / r.clicks) * 100) / 100 : null
  r.custo_unidade = r.unidades > 0 ? Math.round((r.cost / r.unidades) * 100) / 100 : null
  return r
}

export interface ResumoGrupo extends Somatorio {
  grupo: string
  campanhas: number
  farol: Record<Farol, number>
}

/** Totais por grupo (soma dos dias, nunca média das médias), ordenados por gasto. */
export function resumoPorGrupo(linhas: Array<LinhaSomavel & { nome: string | null; farol: Farol }>): ResumoGrupo[] {
  const g = new Map<string, Array<LinhaSomavel & { farol: Farol }>>()
  for (const l of linhas) {
    const k = grupoDaCampanha(l.nome)
    const arr = g.get(k) || []
    arr.push(l)
    g.set(k, arr)
  }
  const out = [...g.entries()].map(([grupo, ls]) => ({ ...somar(ls), grupo, campanhas: ls.length, farol: contarFarol(ls) }))
  out.sort((a, b) => b.cost - a.cost)
  return out
}
